import { AdditionalInfoView } from '../types';
import { Virtualizer, VirtualItem } from '@tanstack/react-virtual';
import { IObservableArray, observe, IArrayDidChange } from 'mobx';
import { useEffect, useLayoutEffect, useRef, MutableRefObject, useState } from 'react';
import { useAppName, AppName } from '@m43/core';

interface LastRenderData {
    lastRenderTopVirtualItemKey: number;
    lastRenderTopVirtualItemInnerOffset: number;
}

type UseScrollAnchorParams = {
    data: IObservableArray<AdditionalInfoView>;
    estimateRowsSize: (rows: AdditionalInfoView[]) => number;
    rowVirtualizer: Virtualizer<HTMLDivElement, Element>;
};

interface Changes {
    addedRows: AdditionalInfoView[];
    removedRows: AdditionalInfoView[];
}

export const useScrollAnchor = ({
    data,
    estimateRowsSize,
    rowVirtualizer,
}: UseScrollAnchorParams): void => {
    const [changes, setChanges] = useState<Changes>({
        addedRows: [],
        removedRows: [],
    });

    const appName = useAppName();

    useEffect(() => {
        // set up a mobx change observer to notify the hook whenever an additional info row is added or removed
        const disposer = observe(data, (change: IArrayDidChange<AdditionalInfoView>) => {
            if (change.type === 'splice') {
                setChanges({ addedRows: change.added, removedRows: change.removed });
            }
        });

        return () => disposer();
        // this should execute on mount only
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const lastRenderDataRef = useRef<LastRenderData>();
    const pendingScrollOffsetRef = useRef<number>();

    const { addedRows, removedRows } = changes;

    if (lastRenderDataRef.current) {
        const {
            lastRenderTopVirtualItemInnerOffset,
            lastRenderTopVirtualItemKey,
        } = lastRenderDataRef.current;

        if (addedRows.length > 0) {
            compensateForAddedRows({
                addedRows,
                // not sure why, but cad-next needs 0px compensation for added rows, whereas cad-client needs the actual rows size
                estimateRowsSize: appName === AppName.CAD_NEXT ? () => 0 : estimateRowsSize,
                lastRenderTopVirtualItemInnerOffset,
                lastRenderTopVirtualItemKey,
                pendingScrollOffsetRef,
                rowVirtualizer,
            });
        }

        if (removedRows.length > 0) {
            compensateForRemovedRows({
                lastRenderTopVirtualItemInnerOffset,
                lastRenderTopVirtualItemKey,
                removedRows,
                rowVirtualizer,
            });
        }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useLayoutEffect(() => {
        let renderNeeded = false;
        if (pendingScrollOffsetRef.current) {
            rowVirtualizer.scrollOffset = pendingScrollOffsetRef.current;
            rowVirtualizer.scrollToOffset(pendingScrollOffsetRef.current, {
                align: 'start',
            });
            pendingScrollOffsetRef.current = undefined;
            // We need to re-render if pendingScrollOffset was used to move the scrollOffset onto a new virtualItem
            // since there would be a blank space until re-render.
            renderNeeded = true;
        }

        // Note: this needs to be a layout effect to ensure that this function runs synchronously when this hook renders
        // which ensures that lastRenderDataRef is always up to date when the component renders
        lastRenderDataRef.current = getLastRenderData(rowVirtualizer, data);

        if (addedRows.length > 0 || removedRows.length > 0) {
            clearChanges(renderNeeded);
        }
    });

    const clearChanges = (rerender: boolean): void => {
        if (rerender) {
            setChanges({ addedRows: [], removedRows: [] });
        } else {
            while (addedRows.length > 0) {
                addedRows.pop();
            }
            while (removedRows.length > 0) {
                removedRows.pop();
            }
        }
    };
};

interface CompensateForAddedRowsProps {
    addedRows: AdditionalInfoView[];
    estimateRowsSize: (rows: AdditionalInfoView[]) => number;
    lastRenderTopVirtualItemInnerOffset: number;
    lastRenderTopVirtualItemKey: number;
    pendingScrollOffsetRef: MutableRefObject<number | undefined>;
    rowVirtualizer: Virtualizer<HTMLDivElement, Element>;
}

const compensateForAddedRows = ({
    addedRows,
    estimateRowsSize,
    lastRenderTopVirtualItemInnerOffset,
    lastRenderTopVirtualItemKey,
    pendingScrollOffsetRef,
    rowVirtualizer,
}: CompensateForAddedRowsProps): void => {
    const lastRenderScrollOffset = rowVirtualizer.scrollOffset;
    if (addedRows[0].id > lastRenderTopVirtualItemKey && lastRenderScrollOffset !== 0) {
        // items were added above current scroll position
        const lastRenderTopVirtualItem = rowVirtualizer.measurementsCache.find(
            ({ key }) => key === lastRenderTopVirtualItemKey
        );

        if (lastRenderTopVirtualItem && rowVirtualizer?.scrollElement) {
            const nextScrollOffset =
                lastRenderTopVirtualItem.start +
                lastRenderTopVirtualItemInnerOffset +
                estimateRowsSize(addedRows);

            const maxScrollOffset =
                rowVirtualizer.scrollElement.scrollHeight -
                rowVirtualizer.scrollElement.clientHeight;

            if (nextScrollOffset > maxScrollOffset) {
                // need to update scrollOffset in useEffect since the scrollbar hasn't expanded yet
                pendingScrollOffsetRef.current = nextScrollOffset;
            } else {
                rowVirtualizer.scrollOffset = nextScrollOffset;
                rowVirtualizer.scrollToOffset(nextScrollOffset, {
                    align: 'start',
                });
            }
        }
    }
};

interface CompenstateForRemovedRowsProps {
    lastRenderTopVirtualItemInnerOffset: number;
    lastRenderTopVirtualItemKey: number;
    removedRows: AdditionalInfoView[];
    rowVirtualizer: Virtualizer<HTMLDivElement, Element>;
}

const compensateForRemovedRows = ({
    lastRenderTopVirtualItemInnerOffset,
    lastRenderTopVirtualItemKey,
    removedRows,
    rowVirtualizer,
}: CompenstateForRemovedRowsProps): void => {
    if (removedRows[0].id > lastRenderTopVirtualItemKey) {
        // items were removed above current scroll position
        const { measurementsCache } = rowVirtualizer;
        const lastRenderTopVirtualItem = measurementsCache.find(
            ({ key }) => key === lastRenderTopVirtualItemKey
        );

        if (lastRenderTopVirtualItem) {
            const sizeAboveTopVirtualItem = getSizeAboveVirtualItemWithRemovedRows(
                lastRenderTopVirtualItem,
                measurementsCache,
                removedRows
            );
            const nextScrollOffset = sizeAboveTopVirtualItem + lastRenderTopVirtualItemInnerOffset;
            rowVirtualizer.scrollOffset = nextScrollOffset;
            rowVirtualizer.scrollToOffset(nextScrollOffset, {
                align: 'start',
            });
        }
    }
};

const getSizeAboveVirtualItemWithRemovedRows = (
    virtualItem: VirtualItem,
    measurementsCache: VirtualItem[],
    removedRows: AdditionalInfoView[]
): number => {
    const itemsAboveVirtualItem = measurementsCache.slice(0, virtualItem.index);
    const removedItemIds = removedRows.map((item) => item.id);
    const itemsAboveVirtualItemAfterRemovedRow = itemsAboveVirtualItem.filter(
        (item) => !removedItemIds.includes(item.key as number)
    );
    return itemsAboveVirtualItemAfterRemovedRow.reduce((result, item) => result + item.size, 0);
};

const getLastRenderData = (
    virtualizer: Virtualizer<HTMLDivElement, Element>,
    data: IObservableArray<AdditionalInfoView>
): LastRenderData | undefined => {
    const firstVirtualItem = virtualizer.getVirtualItems()[0];

    if (
        firstVirtualItem === undefined ||
        data === undefined ||
        data[firstVirtualItem.index] === undefined
    ) {
        return;
    }
    return {
        lastRenderTopVirtualItemKey: data[firstVirtualItem.index].id,
        lastRenderTopVirtualItemInnerOffset:
            Math.ceil(virtualizer.scrollOffset) - firstVirtualItem.start,
    };
};
